import { List } from "immutable";

import { getContentModuleDifficulty } from "seneca-common/features/course/state";
import {
  getContentModuleId,
  wasCorrect,
  wasIncorrect
} from "seneca-common/features/session/selectors/state/sessionModules";

import {
  getSessionModuleIds,
  getTestedSessionModuleIds
} from "../../../selectors/session";

export function getModuleDifficulty(
  state: any,
  sessionId: string,
  moduleId: string
) {
  return getContentModuleDifficulty(
    state,
    getContentModuleId(state, sessionId, moduleId)
  );
}

export function filterModulesByDifficulty(state: any, difficulty: number) {
  return (moduleId: string) =>
    getContentModuleDifficulty(state, moduleId) === difficulty;
}

export function filterFirstModuleIfShown(
  firstModuleId: string,
  sessionModuleIds: List<string | undefined>
) {
  return (moduleId: string) =>
    !(sessionModuleIds.size > 0 && firstModuleId === moduleId);
}

export function incrementDifficulty(
  state: any,
  contentModuleIds: List<string>,
  previousDifficulty: number
) {
  const nextDifficulty = contentModuleIds
    .map(
      moduleId => (getContentModuleDifficulty(state, moduleId) as number) ?? 0
    )
    .filter(difficulty => difficulty > previousDifficulty)
    .sort()
    .first();

  return contentModuleIds.filter(
    filterModulesByDifficulty(state, nextDifficulty ?? previousDifficulty)
  );
}

export function getMinimumDifficulty(
  state: any,
  contentModuleIds: List<string>
) {
  return Math.min(
    ...(contentModuleIds
      .map(moduleId => getContentModuleDifficulty(state, moduleId) ?? 0)
      .toJS() as number[])
  );
}

export function getLastNModuleIds(state: any, sessionId: string, n: number) {
  return getTestedSessionModuleIds(state, sessionId).takeLast(n);
}

export function getLastNTestedModuleIds(
  state: any,
  sessionId: string,
  n: number
) {
  return getTestedSessionModuleIds(state, sessionId).takeLast(n);
}

export function noModulesTested(state: any, sessionId: string) {
  const sessionModuleIds = getTestedSessionModuleIds(state, sessionId);
  return sessionModuleIds.size === 0;
}

export function lessThan2ModulesShown(state: any, sessionId: string) {
  const sessionModuleIds = getSessionModuleIds(state, sessionId);
  return sessionModuleIds.size < 2;
}

export function last2ModulesOfSameDifficultyCorrect(
  state: any,
  sessionId: string
) {
  const last2SessionModuleIds = getLastNModuleIds(state, sessionId, 2);

  const previousDifficulty = getModuleDifficulty(
    state,
    sessionId,
    last2SessionModuleIds.last()!
  );

  return (
    last2SessionModuleIds.size === 2 &&
    last2SessionModuleIds.every(
      id =>
        wasCorrect(state, sessionId, id) &&
        getModuleDifficulty(state, sessionId, id!) === previousDifficulty
    )
  );
}

export function lastModuleWasIncorrectAndNotRepeatOfModuleBefore(
  state: any,
  sessionId: string
) {
  const last2SessionModuleIds = getLastNTestedModuleIds(state, sessionId, 2);

  return (
    last2SessionModuleIds.size === 2 &&
    wasIncorrect(state, sessionId, last2SessionModuleIds.last()) &&
    getContentModuleId(state, sessionId, last2SessionModuleIds.first()) !==
      getContentModuleId(state, sessionId, last2SessionModuleIds.last())
  );
}

export function lastModuleWasCorrectAfterDifficultyDecrease(
  state: any,
  sessionId: string
) {
  const last2SessionModuleIds = getLastNTestedModuleIds(state, sessionId, 2);

  return (
    wasCorrect(state, sessionId, last2SessionModuleIds.last()) &&
    getModuleDifficulty(state, sessionId, last2SessionModuleIds.last()!) <
      getModuleDifficulty(state, sessionId, last2SessionModuleIds.first()!)
  );
}
